/**
 * UranIA PA — `/workspace/mod5` index route.
 *
 * Mostra:
 *   1. Hero compatto "Ambiente Immersivo".
 *   2. CTA "Nuovo documento".
 *   3. Tabella documenti del tenant (titolo, tipo, stato, ultimo aggiornamento)
 *      con filtro testuale + filtro per tipo. Apre il documento in editor
 *      cliccando la riga.
 *   4. Guard onboarding: il funzionario deve aver completato l'onboarding
 *      prima di accedere.
 *
 * Endpoint backend: `GET /api/v1/documenti` (workspace service).
 */

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createRoute, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import type { JSX } from 'react';

import { OnboardingRequiredScreen } from '../../components/workspace/OnboardingRequiredScreen.tsx';
import { selectIsAuthenticated, useAuthStore } from '../../lib/auth/auth-store.ts';
import { useFunzionarioOnboardingStatus } from '../../lib/onboarding/use-funzionario-onboarding-status.ts';
import {
  DocumentoApiError,
  listDocumenti,
  type DocumentoDto,
  type ListDocumentiDto,
} from '../../lib/workspace/documenti-api.ts';
import { Route as ServiziSegmentRoute } from '../_servizi.tsx';

export const WORKSPACE_MOD5_INDEX_PATH = '/workspace/mod5';
const NEW_DOC_PLACEHOLDER_ID = 'new' as const;

const TIPO_OPTIONS: readonly string[] = [
  'base_giuridica',
  'avviso',
  'decreto_graduatoria',
  'decreto_finanziamento',
  'checklist_ricevibilita',
  'checklist_ammissibilita',
];

function StatoBadge(props: { readonly stato: string }): JSX.Element {
  const map: Record<string, { bg: string; fg: string }> = {
    bozza: { bg: '#cfe2ff', fg: '#084298' },
    in_revisione: { bg: '#fff3cd', fg: '#664d03' },
    approvato: { bg: '#d1e7dd', fg: '#0f5132' },
    archiviato: { bg: '#e9ecef', fg: '#495057' },
  };
  const c = map[props.stato] ?? { bg: '#e9ecef', fg: '#495057' };
  return (
    <span
      className="badge"
      style={{ backgroundColor: c.bg, color: c.fg, fontSize: '0.65rem' }}
    >
      {props.stato}
    </span>
  );
}

function formatDate(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

function tipoLabel(
  t: ReturnType<typeof useTranslation>['t'],
  tipo: string,
): string {
  // I tipi sono allineati a admin.banca_dati.categorie? No: sono `tipo_documento`.
  // i18n: workspace.mod5.ribbon.documenti.<tipo>
  return t(`workspace.mod5.ribbon.documenti.${tipo}`, { defaultValue: tipo });
}

interface DocumentiTableProps {
  readonly documenti: readonly DocumentoDto[];
}

function DocumentiTable(props: DocumentiTableProps): JSX.Element {
  const { t } = useTranslation();
  return (
    <div className="card shadow-sm border-0" data-testid="mod5-documenti-card">
      <div className="card-header bg-white border-bottom py-2 px-3">
        <span
          className="text-uppercase text-muted fw-semibold"
          style={{ fontSize: '0.65rem', letterSpacing: '0.05em' }}
        >
          {t('workspace.mod5.index.list_heading', { count: props.documenti.length })}
        </span>
      </div>
      <div className="table-responsive">
        <table
          className="table table-hover mb-0 align-middle"
          data-testid="mod5-documenti-table"
        >
          <thead>
            <tr>
              <th>{t('workspace.mod5.index.columns.titolo')}</th>
              <th>{t('workspace.mod5.index.columns.tipo')}</th>
              <th>{t('workspace.mod5.index.columns.stato')}</th>
              <th>{t('workspace.mod5.index.columns.updated_at')}</th>
              <th className="text-end">
                <span className="visually-hidden">
                  {t('workspace.mod5.index.columns.actions')}
                </span>
              </th>
            </tr>
          </thead>
          <tbody>
            {props.documenti.length === 0 ? (
              <tr>
                <td
                  colSpan={5}
                  className="text-center text-muted small fst-italic py-4"
                >
                  {t('workspace.mod5.index.empty_filter')}
                </td>
              </tr>
            ) : (
              props.documenti.map((d) => (
                <tr key={d.id} data-testid={`mod5-documento-row-${d.id}`}>
                  <td>
                    <strong>{d.titolo}</strong>
                    <br />
                    <code className="small text-muted">{d.id}</code>
                  </td>
                  <td>{tipoLabel(t, d.tipo)}</td>
                  <td>
                    <StatoBadge stato={d.stato} />
                  </td>
                  <td className="small">{formatDate(d.updated_at)}</td>
                  <td className="text-end">
                    <Link
                      to="/workspace/mod5/$docId"
                      params={{ docId: d.id }}
                      className="btn btn-sm btn-primary"
                      data-testid={`mod5-documento-open-${d.id}`}
                    >
                      {t('workspace.mod5.index.actions.open')}
                    </Link>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Mod5IndexPage(): JSX.Element {
  const { t } = useTranslation();
  const isAuthenticated = useAuthStore(selectIsAuthenticated);
  const onboardingStatus = useFunzionarioOnboardingStatus();

  const [search, setSearch] = useState('');
  const [tipoFilter, setTipoFilter] = useState<string>('');

  const query = useQuery<ListDocumentiDto, DocumentoApiError>({
    queryKey: ['workspace', 'documenti'],
    queryFn: listDocumenti,
    enabled: isAuthenticated && onboardingStatus.kind !== 'incomplete',
    staleTime: 5_000,
  });

  const filtered = useMemo(() => {
    const items = query.data?.items ?? [];
    const q = search.trim().toLowerCase();
    return items.filter((d) => {
      if (tipoFilter.length > 0 && d.tipo !== tipoFilter) return false;
      if (q.length === 0) return true;
      return (
        d.titolo.toLowerCase().includes(q) ||
        d.id.toLowerCase().includes(q) ||
        d.tipo.toLowerCase().includes(q) ||
        d.stato.toLowerCase().includes(q)
      );
    });
  }, [query.data?.items, search, tipoFilter]);

  if (!isAuthenticated) {
    return (
      <section
        className="urania-mod5-index"
        data-testid="mod5-index-unauthenticated"
        aria-label={t('workspace.mod5.index.unauthenticated.aria_label')}
      >
        <h1 className="display-6 mb-3">{t('workspace.mod5.index.title')}</h1>
        <div className="alert alert-info" role="region">
          <p className="mb-2">{t('workspace.mod5.index.unauthenticated.message')}</p>
          <Link to="/accedi" className="btn btn-primary" data-testid="mod5-index-go-to-login">
            {t('workspace.mod5.index.unauthenticated.go_to_login')}
          </Link>
        </div>
      </section>
    );
  }

  if (onboardingStatus.kind === 'loading') {
    return <OnboardingRequiredScreen phase="loading" />;
  }
  if (onboardingStatus.kind === 'incomplete') {
    return <OnboardingRequiredScreen phase="incomplete" />;
  }
  if (onboardingStatus.kind === 'error') {
    return (
      <OnboardingRequiredScreen phase="error" errorMessage={onboardingStatus.message} />
    );
  }

  const totalKnown = query.data?.total ?? 0;
  const showingFiltered = filtered.length;
  const hasFilters = search.length > 0 || tipoFilter.length > 0;

  return (
    <section
      className="urania-mod5-index container py-3"
      data-testid="mod5-index-page"
      aria-label={t('workspace.mod5.index.page_aria_label')}
    >
      <div
        className="rounded mb-3 px-3 px-md-4 py-2 py-md-3 position-relative overflow-hidden"
        style={{
          background: 'linear-gradient(135deg, #003d82 0%, #0066cc 100%)',
          color: '#fff',
        }}
      >
        <div className="d-flex justify-content-between align-items-start gap-3 flex-wrap">
          <div style={{ maxWidth: 720 }}>
            <span
              className="badge mb-1"
              style={{
                backgroundColor: 'rgba(255,255,255,0.18)',
                color: '#fff',
                fontSize: '0.65rem',
                letterSpacing: '0.05em',
              }}
            >
              {t('workspace.mod5.index.badge')}
            </span>
            <h1 className="h5 mb-1">{t('workspace.mod5.index.title')}</h1>
            <p
              className="mb-0 opacity-90 small"
              style={{ fontSize: '0.78rem' }}
            >
              {t('workspace.mod5.index.subtitle')}
            </p>
          </div>
          <Link
            to="/workspace/mod5/$docId"
            params={{ docId: NEW_DOC_PLACEHOLDER_ID }}
            className="btn btn-light"
            data-testid="mod5-index-new-document"
          >
            + {t('workspace.mod5.index.new_document_cta')}
          </Link>
        </div>
      </div>

      <div className="card shadow-sm border-0 mb-3">
        <div className="card-body py-2 px-3">
          <div className="row g-2 align-items-end">
            <div className="col-md-7">
              <label className="form-label small text-muted mb-1">
                {t('workspace.mod5.index.filters.search_label')}
              </label>
              <input
                type="search"
                className="form-control form-control-sm"
                value={search}
                onChange={(e): void => setSearch(e.target.value)}
                placeholder={t('workspace.mod5.index.filters.search_placeholder')}
                data-testid="mod5-index-filter-search"
              />
            </div>
            <div className="col-md-4">
              <label className="form-label small text-muted mb-1">
                {t('workspace.mod5.index.filters.tipo_label')}
              </label>
              <select
                className="form-select form-select-sm"
                value={tipoFilter}
                onChange={(e): void => setTipoFilter(e.target.value)}
                data-testid="mod5-index-filter-tipo"
              >
                <option value="">
                  {t('workspace.mod5.index.filters.tipo_all')}
                </option>
                {TIPO_OPTIONS.map((tipo) => (
                  <option key={tipo} value={tipo}>
                    {tipoLabel(t, tipo)}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-1 d-flex justify-content-end">
              {hasFilters && (
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  onClick={(): void => {
                    setSearch('');
                    setTipoFilter('');
                  }}
                  title={t('workspace.mod5.index.filters.reset')}
                  data-testid="mod5-index-filter-reset"
                >
                  ✕
                </button>
              )}
            </div>
          </div>
          {hasFilters && (
            <p className="small text-muted mb-0 mt-2">
              {t('workspace.mod5.index.filters.results_count', {
                shown: showingFiltered,
                total: totalKnown,
              })}
            </p>
          )}
        </div>
      </div>

      {query.isLoading && (
        <p role="status" aria-live="polite">
          {t('workspace.mod5.index.loading')}
        </p>
      )}
      {query.isError && (
        <div className="alert alert-danger" role="alert">
          <strong>{t('workspace.mod5.index.errors.load_failed')}</strong>
          {query.error.detail !== null && <p className="mb-0">{query.error.detail}</p>}
        </div>
      )}
      {query.isSuccess && query.data.total === 0 ? (
        <div
          className="alert alert-info"
          role="region"
          data-testid="mod5-index-empty"
        >
          <strong>{t('workspace.mod5.index.empty_title')}</strong>
          <p className="mb-0 small">{t('workspace.mod5.index.empty_body')}</p>
        </div>
      ) : query.isSuccess ? (
        <DocumentiTable documenti={filtered} />
      ) : null}
    </section>
  );
}

export const Route = createRoute({
  getParentRoute: () => ServiziSegmentRoute,
  path: WORKSPACE_MOD5_INDEX_PATH,
  component: Mod5IndexPage,
});
